import * as React from "react";
import { cn } from "@/lib/cn";
import { Skeleton } from "./Feedback";

/**
 * The console's list surface. Appearance is `.table*` in `styles/ui.css`.
 *
 * A real `<table>` rather than a stack of flex rows, because these lists are
 * genuinely tabular - a screen reader should be able to announce "Role, column
 * 3" and a sighted user should be able to read down a column. A div grid gives
 * up both for nothing.
 *
 * **The wrapper scrolls, the page does not.** A table with five columns will
 * overflow a phone; without `overflow-x-auto` on the wrapper that overflow
 * becomes a horizontal scrollbar under the whole document, which drags the
 * sidebar and the topbar sideways with it.
 *
 * Columns that are optional on a narrow screen carry `hideBelow` and disappear
 * rather than compressing, because a name squeezed to two characters is worse
 * than a name with no timezone next to it.
 */

type Breakpoint = "sm" | "md" | "lg";
type Align = "left" | "center" | "right";

/** `left` is the stylesheet default, so only the other two need a class. */
const ALIGN: Record<Align, string> = {
  left: "",
  center: "align-center",
  right: "align-right",
};

export function Table({
  head,
  minWidth = "sm",
  children,
  className,
}: {
  head: React.ReactNode;
  /** How narrow the table may get before the wrapper scrolls instead. */
  minWidth?: "sm" | "md" | "lg";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("table-wrap", className)}>
      <table className={cn("table", minWidth !== "sm" && `table--w-${minWidth}`)}>
        <thead>
          <tr className="table__head-row">{head}</tr>
        </thead>
        <tbody className="table__body">{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  hideBelow,
  align = "left",
  className,
}: {
  children?: React.ReactNode;
  hideBelow?: Breakpoint;
  align?: Align;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn("table__th", ALIGN[align], hideBelow && `hide-below-${hideBelow}`, className)}
    >
      {children}
    </th>
  );
}

export function Tr({ children, className, ...rest }: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className={cn("table__row", className)} {...rest}>
      {children}
    </tr>
  );
}

/**
 * A body cell.
 *
 * `scope="row"` renders a `<th>` instead, for the cell that names its row - the
 * feature in a comparison matrix, the role in a permissions grid. It is a `Td`
 * rather than a `Th` because it belongs to the body and wears body styling; only
 * the element and the scope change, which is what lets a screen reader announce
 * "Offline capture, Crew, included" instead of reading a bare "included".
 */
export function Td({
  children,
  hideBelow,
  align = "left",
  valign = "middle",
  scope,
  className,
}: {
  children?: React.ReactNode;
  hideBelow?: Breakpoint;
  align?: Align;
  /** `top` when neighbouring cells hold several lines and this one holds a label. */
  valign?: "middle" | "top";
  scope?: "row";
  className?: string;
}) {
  const Cell = scope === "row" ? "th" : "td";

  return (
    <Cell
      scope={scope}
      className={cn(
        "table__td",
        valign === "top" && "table__td--top",
        scope === "row" && "table__td--row-header",
        ALIGN[align],
        hideBelow && `hide-below-${hideBelow}`,
        className
      )}
    >
      {children}
    </Cell>
  );
}

/**
 * Placeholder rows while a list loads.
 *
 * The shape is described rather than drawn: `"identity"` is the mark-plus-lines
 * first cell every table in this console opens with, `"text"` is a single bar,
 * `"badge"` is a pill, and `"blank"` is a column that stays empty - an actions
 * cell has nothing to promise, so promising a bar there is a small lie.
 *
 * **`hideBelow` and `round` must match the real row's.** A skeleton showing a
 * column the loaded table then removes makes the table jump on arrival, which is
 * the one thing a skeleton exists to prevent.
 */
type SkeletonColumn = {
  kind: "identity" | "text" | "badge" | "blank";
  /** `wide` for text, `narrow` for a badge. */
  width?: "wide" | "narrow";
  /** Match the real row: `Avatar` is round, `IconTile` is not. */
  round?: boolean;
  hideBelow?: Breakpoint;
};

export function SkeletonRows({ rows = 3, columns }: { rows?: number; columns: SkeletonColumn[] }) {
  return (
    <>
      {Array.from({ length: rows }, (_, row) => (
        <Tr key={row}>
          {columns.map((column, index) => (
            <Td key={index} hideBelow={column.hideBelow}>
              {column.kind === "identity" && (
                <div className="table__skeleton-identity">
                  <Skeleton
                    className={cn(
                      "table__skeleton-mark",
                      column.round && "table__skeleton-mark--round"
                    )}
                  />
                  <div>
                    <Skeleton className="table__skeleton-line" />
                    <Skeleton className="table__skeleton-subline" />
                  </div>
                </div>
              )}
              {column.kind === "text" && (
                <Skeleton
                  className={cn(
                    "table__skeleton-text",
                    column.width === "wide" && "table__skeleton-text--wide"
                  )}
                />
              )}
              {column.kind === "badge" && (
                <Skeleton
                  className={cn(
                    "table__skeleton-badge",
                    column.width === "narrow" && "table__skeleton-badge--narrow"
                  )}
                />
              )}
            </Td>
          ))}
        </Tr>
      ))}
    </>
  );
}

/** Full-width cell for an empty, loading or error state inside a table body. */
export function TdSpan({ children, colSpan }: { children: React.ReactNode; colSpan: number }) {
  return (
    <td colSpan={colSpan} className="table__span">
      {children}
    </td>
  );
}
